<script setup lang="ts" generic="T extends string">
/**
 * The dropdown body both select-shaped inputs render: the optional clear row,
 * the options with their multi-select checkmarks, and the "nothing matched"
 * hint. Internal to the Inputs folder — not exported from the package, because
 * on its own it is not a field.
 *
 * It owns the highlight's scroll-into-view, since it owns the DOM the highlight
 * lives in. Navigation itself stays with the trigger components: what a key does
 * depends on whether the trigger types or not.
 *
 * Rows suppress the mousedown default so picking an option never moves focus off
 * the trigger — a row is a plain div, so without it the browser blurs the field,
 * and a multi-select, which stays open after a pick, would be left unfocused and
 * unnavigable. `NxDropdown` leaves this to its consumers on purpose (its
 * docblock explains why a built-in closer would swallow the selection).
 *
 * Rows are keyed by their position in the *source* list, which a filtering caller
 * passes in `keys`. That is the only identifier with both properties this list
 * needs: unique even when two options carry the same label (AdminIdSelect maps
 * records to their labels, and two records may share one), and unchanged while a
 * query narrows the list — so a surviving row is patched instead of being
 * unmounted and rebuilt on every keystroke. Keying by the value alone collapses
 * duplicates into one vnode; keying by the position in the *filtered* list lets a
 * row's vnode carry slot state over to a different option.
 *
 * `optionGroup` draws a heading wherever the group changes from one option to
 * the next. Headings are rows between the options, not options: they take no
 * index, so the highlight, the arrow keys and the typeahead — all counting
 * options — pass over them without knowing they exist. The caller hands the
 * options in group order (`NxSelectInput` sorts them).
 */
import { CheckmarkIcon } from '@blpde/nx-icons';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  /** Optional ARIA owner id supplied by a combobox trigger. */
  id?: string;
  /** The options to render — already filtered by the caller. */
  options: T[];
  /**
   * Row identity: each option's index in the caller's unfiltered list. Omit when
   * the caller does not filter — then the render index already is that position.
   */
  keys?: readonly number[];
  /** The current selection, for the multi-select checkmarks. */
  selected: T[];
  /**
   * Plain-text twin of an option, used as the default row text. Without it a
   * consumer that supplies `optionText` alone would filter and title by the
   * label while the rows still read the raw value.
   */
  optionText: (option: T) => string;
  optionDisabled?: (option: T) => boolean;
  multiple?: boolean;
  allowNull?: boolean;
  /** Whether the clear row is offered at all (hidden while a query is active). */
  showPlaceholder?: boolean;
  placeholder: string;
  /** Font size of the dropdown, derived from the field height. */
  fontSize: string;
  /** Shown when `options` is empty. Omit to render nothing instead. */
  emptyText?: string;
  /** Group heading of an option; `undefined` or `''` = no heading. */
  optionGroup?: (option: T) => string | undefined;
}>();

const emit = defineEmits<{
  select: [option: T];
  selectPlaceholder: [];
}>();

const highlightedIndex = defineModel<number>('highlightedIndex', { required: true });

const dropdownRef = ref<HTMLElement>();

type OptionRow = { option: T; index: number };
type OptionSection = { key: string; label: string | undefined; rows: OptionRow[] };

/**
 * Runs of consecutive options with the same group. Without `optionGroup` this
 * is one unlabelled run, so an ungrouped list renders exactly its option rows.
 */
const sections = computed<OptionSection[]>(() => {
  const group = props.optionGroup;
  if (!group) {
    return [
      {
        key: '',
        label: undefined,
        rows: props.options.map((option, index) => ({ option, index })),
      },
    ];
  }
  const out: OptionSection[] = [];
  props.options.forEach((option, index) => {
    const label = group(option) || undefined;
    const last = out.at(-1);
    if (last && last.label === label) last.rows.push({ option, index });
    else out.push({ key: `${out.length}:${label ?? ''}`, label, rows: [{ option, index }] });
  });
  return out;
});

/** Heading id — only in listbox mode, where options point at it. */
function headingId(sectionIndex: number): string | undefined {
  return props.id ? `${props.id}-group-${sectionIndex}` : undefined;
}

function scrollHighlightIntoView() {
  nextTick(() => {
    const row = dropdownRef.value?.querySelector('.select-item.highlighted');
    // The first option of a group brings its heading into view first — arrowing
    // up into a group would otherwise hide which group it is.
    const heading = row?.previousElementSibling;
    if (heading?.classList.contains('select-group-heading')) {
      heading.scrollIntoView({ block: 'nearest' });
    }
    row?.scrollIntoView({ block: 'nearest' });
  });
}

// `listElement`: the trigger components need the real element for their
// click-outside check. `scrollHighlightIntoView`: they call it when they open,
// because the watcher below only fires when the index *changes* — reopening at
// the index it was left on would otherwise show the list wherever an inside
// scroll had pushed it, with the highlighted row out of sight and Enter armed.
defineExpose({ listElement: dropdownRef, scrollHighlightIntoView });

watch(highlightedIndex, scrollHighlightIntoView);
</script>

<template>
  <div
    :id="props.id"
    ref="dropdownRef"
    class="select-dropdown"
    :role="props.id ? 'listbox' : undefined"
    :style="`--font-size: ${props.fontSize}`"
  >
    <!-- Single-select: placeholder option to clear -->
    <div
      v-if="!props.multiple && props.showPlaceholder"
      :id="props.id ? `${props.id}-placeholder` : undefined"
      :role="props.id ? 'option' : undefined"
      :aria-disabled="!props.allowNull || undefined"
      :aria-selected="props.id ? false : undefined"
      :class="[
        'select-item',
        'placeholder',
        'single',
        { highlighted: highlightedIndex === -1, disabled: !props.allowNull },
      ]"
      @mousedown.prevent
      @click="emit('selectPlaceholder')"
      @pointerenter="highlightedIndex = -1"
    >
      <slot name="placeholder" :placeholder="props.placeholder">
        {{ props.placeholder }}
      </slot>
    </div>
    <template v-for="(section, sectionIndex) in sections" :key="section.key">
      <!-- Not an option: no index, no click, no highlight. In listbox mode the
           options name it as their description instead of the listbox reading
           it out as a stray text row between them. -->
      <div
        v-if="section.label"
        :id="headingId(sectionIndex)"
        class="select-group-heading"
        :aria-hidden="props.id ? 'true' : undefined"
        @mousedown.prevent
      >
        {{ section.label }}
      </div>
      <div
        v-for="{ option, index } in section.rows"
        :id="props.id ? `${props.id}-option-${props.keys?.[index] ?? index}` : undefined"
        :key="props.keys?.[index] ?? index"
        :role="props.id ? 'option' : undefined"
        :aria-selected="props.id ? props.selected.includes(option) : undefined"
        :aria-disabled="props.optionDisabled?.(option) || undefined"
        :aria-describedby="section.label ? headingId(sectionIndex) : undefined"
        :class="[
          'select-item',
          {
            highlighted: highlightedIndex === index,
            multiple: props.multiple,
            disabled: props.optionDisabled?.(option),
          },
        ]"
        @mousedown.prevent
        @click="!props.optionDisabled?.(option) && emit('select', option)"
        @pointerenter="!props.optionDisabled?.(option) && (highlightedIndex = index)"
      >
        <div v-if="props.multiple && props.selected.includes(option)" class="select-item-indicator">
          <CheckmarkIcon filled />
        </div>
        <slot name="option" :option="option">
          {{ props.optionText(option) }}
        </slot>
      </div>
    </template>
    <div v-if="props.emptyText && props.options.length === 0" class="select-empty">
      {{ props.emptyText }}
    </div>
  </div>
</template>

<style scoped>
.select-dropdown {
  font-size: var(--font-size);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-2xs);
  background-color: var(--nxt-popover-bg);
  border: solid var(--nxt-hairline) var(--nxt-popover-border);
  box-shadow: var(--nxt-popover-shadow);
  display: flex;
  flex-direction: column;
  /* Tall enough for a 10-entry enum (line types) without a cramped inner scroll
     box; longer catalogs still scroll. Raised from 10em by I-180. */
  max-height: 21em;
  overflow-y: auto;
  user-select: none;
}

.select-item {
  font-size: 0.875em;
  font-weight: var(--nxt-font-weight-medium);
  position: relative;
  color: var(--nxt-menu-item-fg);
  display: inline-flex;
  justify-content: flex-start;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-sm);
  cursor: pointer;
  background-color: transparent;
  white-space: nowrap;

  &.disabled {
    color: var(--nxt-fg-subtle);
    cursor: default;
  }

  &.highlighted {
    background-color: var(--nxt-menu-item-bg-active);
    color: var(--nxt-menu-item-fg);
  }

  &.placeholder {
    color: var(--nxt-fg-subtle);

    &.disabled {
      pointer-events: none;
    }
  }

  &.multiple {
    padding-inline-start: calc(
      var(--nxt-space-xs) + 1em + var(--nxt-space-2xs)
    ); /* base + icon + extra-padding */
  }
}

.select-item-indicator {
  color: var(--nxt-select-icon);
  position: absolute;
  left: calc(var(--nxt-space-2xs) / 2);
  top: 50%;
  transform: translateY(calc(-50% + 0.125em)); /* +0.125em as the icon is somewhat off-center */
  padding-inline-start: var(--nxt-space-2xs); /* extra-padding */
}

/* Same voice as the command palette's group label: small caps-like, subtle. */
.select-group-heading {
  font-size: 0.75em;
  font-weight: var(--nxt-font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nxt-fg-subtle);
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-2xs);
  white-space: nowrap;
  cursor: default;
}

.select-empty {
  font-size: 0.875em;
  font-weight: var(--nxt-font-weight-medium);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  color: var(--nxt-fg-subtle);
}
</style>
